import { ContextoPWA } from '@core/context/services/mobile-context.service';
import { encontrarPorIdOuFallback } from '@shared/list-search.util';
import {
  AnoLetivoOption,
  ComponenteOption,
  EntidadeOption,
  EstabelecimentoOption,
  ServidorOption,
  TurmaOption
} from './context-selector-modal.model';

export interface HeadersContextuais {
  userAccessId?: string;
  contexto?: any;
}

/**
 * Monta os headers HTTP (User-Access + app-context) a partir dos valores
 * RECÉM-selecionados no modal (não do localStorage) — usados nas chamadas de
 * carregamento em cascata pra garantir a filtragem correta no backend.
 */
export function construirHeadersContextuais(
  entidadeSelecionadaData: EntidadeOption | null,
  servidorSelecionado: ServidorOption['id'] | null,
  anoLetivoSelecionado: AnoLetivoOption['id'] | null,
  estabelecimentoSelecionado: EstabelecimentoOption['id'] | null,
  turmaSelecionada: TurmaOption['id'] | null
): HeadersContextuais {
  const headers: HeadersContextuais = {};

  if (entidadeSelecionadaData?.licenseId) {
    headers.userAccessId = entidadeSelecionadaData.licenseId;
  }

  const contextoNovo: any = {};

  if (servidorSelecionado) {
    contextoNovo.servidor = servidorSelecionado;
  }

  if (anoLetivoSelecionado) {
    contextoNovo.anoLetivo = anoLetivoSelecionado;
  }

  if (estabelecimentoSelecionado) {
    contextoNovo.estabelecimento = estabelecimentoSelecionado;
  }

  if (turmaSelecionada) {
    contextoNovo.turma = turmaSelecionada;
  }

  if (Object.keys(contextoNovo).length > 0) {
    headers.contexto = contextoNovo;
  }

  return headers;
}

/**
 * Mapeia a resposta bruta de `getAnosLetivos` pro shape usado no modal.
 * Extraído pra evitar duplicação — usado tanto na cascata de restauração
 * quanto no carregamento normal (usuário troca de servidor manualmente).
 */
export function mapearAnosLetivos(anos: { id: number; ano: string | number; descricao?: string }[]): AnoLetivoOption[] {
  return anos.map(a => ({
    id: a.id,
    ano: a.ano,
    descricao: a.descricao
  }));
}

/**
 * Nível 3 da cascata de restauração: dado o ano letivo já carregado e o
 * `ContextoPWA` salvo, decide qual ano letivo deve ficar pré-selecionado
 * (por id ou, na falta dele, por ano). Retorna `null` quando não encontrado —
 * quem chama decide se isso é um erro que aborta a cascata.
 */
export function selecionarAnoLetivoRestaurado(
  anosLetivos: AnoLetivoOption[],
  contexto: ContextoPWA
): AnoLetivoOption['id'] | null {
  const anoParaSelecionar = encontrarPorIdOuFallback(
    anosLetivos,
    a => contexto.anoLetivo?.id != null && (a.id === contexto.anoLetivo?.id || Number(a.id) === Number(contexto.anoLetivo?.id)),
    a => !!contexto.anoLetivo?.ano && String(a.ano) === String(contexto.anoLetivo?.ano)
  );
  return anoParaSelecionar ? anoParaSelecionar.id : null;
}

/**
 * Nível 1 da cascata (entidade): dedup da resposta bruta de `listEntidades` por
 * entityId — a API pode retornar a mesma entidade mais de uma vez por access diferente.
 */
export function mapearEntidadesUnicas(
  entidades: Array<{ id: number; nome: string; licenseId: string; access: string; admin?: boolean }>
): EntidadeOption[] {
  const entidadesUnicas = new Map<number, EntidadeOption>();
  entidades.forEach((e) => {
    const entityId = Number(e.id);
    if (!entidadesUnicas.has(entityId)) {
      entidadesUnicas.set(entityId, {
        id: e.id,
        nome: e.nome || 'Entidade sem nome',
        licenseId: e.licenseId,
        access: e.access,
        admin: e.admin
      });
    }
  });
  return Array.from(entidadesUnicas.values());
}

/**
 * Nível 1 da cascata de restauração: decide qual entidade (já deduplicada) deve
 * ficar pré-selecionada, por id ou, na falta dele, por nome. É CRÍTICO — se
 * retornar `null`, quem chama aborta a cascata inteira (ver `restaurarContextoCompleto`).
 * Diferente dos demais níveis, retorna a `EntidadeOption` inteira (não só o id)
 * porque o nível de servidor (nível 2) precisa do `licenseId` dela.
 */
export function selecionarEntidadeRestaurada(
  entidades: EntidadeOption[],
  contexto: ContextoPWA
): EntidadeOption | null {
  return encontrarPorIdOuFallback(
    entidades,
    (e) => contexto.entidade?.id != null && (e.id === contexto.entidade?.id || Number(e.id) === Number(contexto.entidade?.id)),
    (e) => !!contexto.entidade?.nome && e.nome === contexto.entidade?.nome
  );
}

/** Mapeia um servidor bruto (professor vinculado ou item de lista genérica) pro shape do modal. */
export function mapearServidor(servidor: { id: number; pessoa: { nome: string } }): ServidorOption {
  return { id: servidor.id, nome: servidor.pessoa.nome };
}

/** Nível 2 da cascata (fluxo secretário): mapeia a lista genérica de servidores. */
export function mapearServidores(servidores: Array<{ id: number; pessoa: { nome: string } }>): ServidorOption[] {
  return servidores.map(mapearServidor);
}

/**
 * Nível 2 da cascata (fluxo secretário): decide qual servidor, da lista genérica já
 * carregada, deve ficar pré-selecionado — por id ou, na falta dele, pelo nome da pessoa.
 * Retorna a `ServidorOption` inteira (não só o id) porque quem chama ainda precisa
 * aplicar o perfil default dela — ver `aplicarServidorRestaurado`.
 */
export function selecionarServidorRestaurado(
  servidores: ServidorOption[],
  contexto: ContextoPWA
): ServidorOption | null {
  return encontrarPorIdOuFallback(
    servidores,
    (s) => contexto.servidor?.id != null && Number(s.id) === Number(contexto.servidor?.id),
    (s) => !!contexto.servidor?.pessoa?.nome && s.nome === contexto.servidor?.pessoa?.nome
  );
}

/**
 * Nível 2, fallback final: quando o servidor salvo não está nos primeiros resultados
 * genéricos (lista limitada), o componente busca de novo filtrando pelo nome salvo —
 * esta função decide se algum resultado desse fallback bate com o servidor procurado
 * (por id ou nome). Mantém a mesma condição do código original (sem guard extra em
 * `servidorId`): `Number(undefined)` é `NaN`, então a comparação por id simplesmente
 * não bate quando não há id salvo.
 */
export function encontrarServidorNoFallback(
  servidoresFallback: Array<{ id: number; pessoa: { nome: string } }>,
  servidorId: number | null | undefined,
  servidorNome: string | null | undefined
): ServidorOption | null {
  const encontrado = servidoresFallback
    .map(mapearServidor)
    .find((s) => Number(s.id) === Number(servidorId) || s.nome === servidorNome);
  return encontrado || null;
}

/** Nível 4 da cascata (estabelecimento): mapeia a resposta bruta de `getEstabelecimentos`. */
export function mapearEstabelecimentos(
  estabelecimentos: Array<{ id: number; descricao: string }>
): EstabelecimentoOption[] {
  return estabelecimentos.map((e) => ({
    id: e.id,
    nome: e.descricao,
    codigo: undefined
  }));
}

/** Nível 4 da cascata: decide qual estabelecimento pré-selecionar, por id ou descrição. */
export function selecionarEstabelecimentoRestaurado(
  estabelecimentos: EstabelecimentoOption[],
  contexto: ContextoPWA
): EstabelecimentoOption['id'] | null {
  const estabelecimentoParaSelecionar = encontrarPorIdOuFallback(
    estabelecimentos,
    (e) => contexto.estabelecimento?.id != null && (e.id === contexto.estabelecimento?.id || Number(e.id) === Number(contexto.estabelecimento?.id)),
    (e) => !!contexto.estabelecimento?.descricao && e.nome === contexto.estabelecimento?.descricao
  );
  return estabelecimentoParaSelecionar ? estabelecimentoParaSelecionar.id : null;
}

/**
 * Nível 5 da cascata (turma): mapeia a resposta bruta de `getTurmas`, filtrando
 * turmas de etapa modular (não aparecem na seleção de turma da frequência mobile).
 */
export function mapearTurmas(
  turmasContent: Array<{
    id: number;
    descricao?: string;
    nome?: string;
    tipoAtendimento?: string;
    modalidade?: string;
    etapaMatriz?: { modular?: boolean } | null;
  }>
): TurmaOption[] {
  return turmasContent
    .filter((t) => !t.etapaMatriz?.modular)
    .map((t) => ({
      id: t.id,
      descricao: t.descricao || t.nome || 'Turma sem descrição',
      nome: t.nome,
      tipoAtendimento: t.tipoAtendimento,
      modalidade: t.modalidade,
      etapaMatriz: t.etapaMatriz ?? null
    }));
}

/** Nível 5 da cascata: decide qual turma pré-selecionar, por id ou descrição/nome. */
export function selecionarTurmaRestaurada(
  turmas: TurmaOption[],
  contexto: ContextoPWA
): TurmaOption['id'] | null {
  const turmaParaSelecionar = encontrarPorIdOuFallback(
    turmas,
    (t) => contexto.turma?.id != null && (t.id === contexto.turma?.id || Number(t.id) === Number(contexto.turma?.id)),
    (t) => !!contexto.turma?.descricao && (t.descricao === contexto.turma?.descricao || t.nome === contexto.turma?.descricao)
  );
  return turmaParaSelecionar ? turmaParaSelecionar.id : null;
}

/** Nível 6 da cascata (componentes): mapeia a resposta bruta de `getComponentesCurriculares`. */
export function mapearComponentes(
  componentes: Array<{ id: number; descricao: string; nome?: string; isCampoExperiencia?: boolean }>
): ComponenteOption[] {
  return componentes.map((c) => ({
    id: c.id,
    descricao: c.descricao || c.nome || 'Componente sem descrição',
    nome: c.nome,
    isCampoExperiencia: c.isCampoExperiencia || false,
    selected: false
  }));
}

/**
 * Nível 6 da cascata: decide, pra cada componente já mapeado, se deve começar
 * selecionado. Retorna um array NOVO (não muta o array recebido) — replica os 3
 * cenários do componente legado:
 * - contexto salvo tem componentes: marca só os que estão na lista salva (por id);
 * - sem componentes salvos + seleção única (turma de atividade/EJA modular):
 *   marca só o primeiro;
 * - sem componentes salvos + turma regular: marca todos.
 */
export function selecionarComponentesRestaurados(
  componentes: ComponenteOption[],
  contexto: ContextoPWA,
  isSelecaoUnica: boolean
): ComponenteOption[] {
  if (contexto.componentes && contexto.componentes.length > 0) {
    const idsContexto = contexto.componentes.map((c) => c.id);
    return componentes.map((comp) => ({ ...comp, selected: idsContexto.includes(comp.id) }));
  }

  if (isSelecaoUnica) {
    return componentes.map((comp, idx) => ({ ...comp, selected: idx === 0 }));
  }

  return componentes.map((comp) => ({ ...comp, selected: true }));
}

/**
 * Monta o `ContextoPWA` a ser persistido a partir dos ids selecionados no modal,
 * resolvendo cada id pro objeto completo correspondente nas listas carregadas.
 * Os componentes mantêm a ordem original do array (`originalIndex`), não a ordem
 * de seleção do usuário.
 */
export function buildContextoPWA(
  entidadeSelecionada: EntidadeOption['id'] | null,
  entidades: EntidadeOption[],
  servidorSelecionado: ServidorOption['id'] | null,
  servidores: ServidorOption[],
  anoLetivoSelecionado: AnoLetivoOption['id'] | null,
  anosLetivos: AnoLetivoOption[],
  estabelecimentoSelecionado: EstabelecimentoOption['id'] | null,
  estabelecimentos: EstabelecimentoOption[],
  turmaSelecionada: TurmaOption['id'] | null,
  turmas: TurmaOption[],
  componentes: ComponenteOption[]
): ContextoPWA {
  return {
    entidade: entidadeSelecionada ? {
      id: entidadeSelecionada,
      nome: entidades.find((e) => e.id === entidadeSelecionada)?.nome || ''
    } : null,
    servidor: servidorSelecionado ? {
      id: servidorSelecionado,
      nome: servidores.find((s) => s.id === servidorSelecionado)?.nome || '',
      pessoa: {
        nome: servidores.find((s) => s.id === servidorSelecionado)?.nome || ''
      }
    } : null,
    anoLetivo: anoLetivoSelecionado ? {
      id: anoLetivoSelecionado,
      ano: anosLetivos.find((a) => a.id === anoLetivoSelecionado)?.ano || ''
    } : null,
    estabelecimento: estabelecimentoSelecionado ? {
      id: estabelecimentoSelecionado,
      nome: estabelecimentos.find((e) => e.id === estabelecimentoSelecionado)?.nome || '',
      descricao: estabelecimentos.find((e) => e.id === estabelecimentoSelecionado)?.nome || ''
    } : null,
    turma: turmaSelecionada ? {
      id: turmaSelecionada,
      descricao: turmas.find((t) => t.id === turmaSelecionada)?.descricao || '',
      nome: turmas.find((t) => t.id === turmaSelecionada)?.nome,
      tipoAtendimento: turmas.find((t) => t.id === turmaSelecionada)?.tipoAtendimento,
      etapaMatriz: turmas.find((t) => t.id === turmaSelecionada)?.etapaMatriz ?? undefined,
      modalidade: turmas.find((t) => t.id === turmaSelecionada)?.modalidade
    } : null,
    componentes: componentes
      .map((c, idx) => c.selected ? {
        id: c.id,
        descricao: c.descricao,
        nome: c.nome,
        isCampoExperiencia: c.isCampoExperiencia,
        originalIndex: idx
      } : null)
      .filter(Boolean) as any
  };
}
